/**
 * KPIProgressChart — Smooth line chart showing check-in performance over time.
 *
 * Renders a gradient-filled area chart with:
 *  - Check-in values plotted along the timeline
 *  - A dashed target line for reference
 *  - Color based on current performance status
 *  - Minimal chrome — designed to fit inline on cards and drawers
 */
import React, { useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';

interface CheckInPoint {
  date: string;
  value: number;
  percentage: number | null;
}

interface KPIProgressChartProps {
  checkIns: CheckInPoint[];
  targetValue: number | null;
  baselineValue: number | null;
  currentPercentage: number | null;
  unit?: string;
  unitLabel?: string;
  height?: number;
  /** 'compact' for cards, 'full' for drawer */
  variant?: 'compact' | 'full';
}

const KPIProgressChart: React.FC<KPIProgressChartProps> = ({
  checkIns,
  targetValue,
  baselineValue,
  currentPercentage,
  unit,
  unitLabel,
  height = 80,
  variant = 'compact',
}) => {
  const pct = currentPercentage ?? 0;
  const lineColor = pct >= 80 ? '#34d399' : pct >= 50 ? '#fbbf24' : '#f87171';

  const data = useMemo(() => {
    if (!checkIns || checkIns.length === 0) return null;

    const sorted = [...checkIns].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    const points = sorted.map((ci) => ({
      x: new Date(ci.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      y: ci.value,
    }));

    if (baselineValue !== null && baselineValue !== undefined && points.length > 0) {
      const firstVal = points[0].y;
      if (Math.abs(firstVal - baselineValue) > 0.01) {
        points.unshift({ x: 'Start', y: baselineValue });
      }
    }

    return [{ id: 'progress', data: points }];
  }, [checkIns, baselineValue]);

  const targetLine = useMemo(() => {
    if (targetValue === null || targetValue === undefined || !data) return null;
    return [
      {
        id: 'target',
        data: data[0].data.map((p) => ({ x: p.x, y: targetValue })),
      },
    ];
  }, [targetValue, data]);

  if (!data || data[0].data.length < 2) return null;

  const allValues = data[0].data.map((p) => p.y);
  if (targetValue !== null && targetValue !== undefined) allValues.push(targetValue);
  if (baselineValue !== null && baselineValue !== undefined) allValues.push(baselineValue);
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const padding = (maxVal - minVal) * 0.15 || 1;

  const isCompact = variant === 'compact';

  const formatAxisValue = (v: number) => {
    if (unit === 'percentage') return `${v}${unitLabel || '%'}`;
    if (unit === 'currency') return `${unitLabel || '$'}${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`;
    if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
    return `${v}`;
  };

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveLine
        data={[
          ...data,
          ...(targetLine || []),
        ]}
        margin={
          isCompact
            ? { top: 4, right: 4, bottom: 4, left: 4 }
            : { top: 8, right: 12, bottom: 28, left: 44 }
        }
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: minVal - padding,
          max: maxVal + padding,
          stacked: false,
        }}
        curve="monotoneX"
        colors={[lineColor, 'transparent']}
        lineWidth={isCompact ? 2.5 : 3}
        enablePoints={!isCompact}
        pointSize={isCompact ? 0 : 7}
        pointColor={lineColor}
        pointBorderWidth={2}
        pointBorderColor="var(--surface-panel-from)"
        enableArea={true}
        areaOpacity={0.12}
        areaBaselineValue={minVal - padding}
        enableGridX={false}
        enableGridY={!isCompact}
        gridYValues={3}
        axisBottom={
          isCompact
            ? null
            : {
                tickRotation: -30,
                tickSize: 0,
                tickPadding: 8,
              }
        }
        axisLeft={
          isCompact
            ? null
            : {
                tickSize: 0,
                tickPadding: 6,
                tickValues: 3,
                format: formatAxisValue,
              }
        }
        axisTop={null}
        axisRight={null}
        useMesh={!isCompact}
        enableCrosshair={!isCompact}
        crosshairType="bottom"
        tooltip={({ point }) => (
          <div className="px-2.5 py-1.5 rounded-lg bg-surface-page border border-default shadow-xl text-xs">
            <span className="text-content-muted">{point.data.xFormatted}: </span>
            <span className="text-content font-semibold">
              {formatAxisValue(point.data.yFormatted as number)}
            </span>
          </div>
        )}
        layers={[
          'grid',
          'axes',
          ({ xScale, yScale }: any) => {
            if (!targetValue) return null;
            const y = yScale(targetValue);
            if (isNaN(y)) return null;
            return (
              <line
                x1={0}
                x2="100%"
                y1={y}
                y2={y}
                stroke="rgba(255,255,255,0.25)"
                strokeWidth={1}
                strokeDasharray="4,4"
              />
            );
          },
          'areas',
          'lines',
          'points',
          'crosshair',
          'mesh',
          ...(!isCompact && targetValue
            ? [
                ({ yScale }: any) => {
                  const y = yScale(targetValue);
                  if (isNaN(y)) return null;
                  return (
                    <text
                      x={8}
                      y={y - 6}
                      fill="rgba(255,255,255,0.4)"
                      fontSize={9}
                      fontFamily="inherit"
                    >
                      Target
                    </text>
                  );
                },
              ]
            : []),
        ]}
        theme={{
          background: 'transparent',
          text: { fill: 'rgba(255,255,255,0.45)', fontSize: 10 },
          grid: {
            line: { stroke: 'rgba(255,255,255,0.07)', strokeWidth: 1 },
          },
          axis: {
            ticks: {
              text: { fill: 'rgba(255,255,255,0.4)', fontSize: 10 },
            },
          },
          crosshair: {
            line: { stroke: 'rgba(255,255,255,0.25)', strokeWidth: 1 },
          },
        }}
      />
    </div>
  );
};

export default React.memo(KPIProgressChart);
